import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import GroupsIcon from '@mui/icons-material/Groups';
import config from '../config';
import RuleScreen from '../shared/RuleScreen';
import FilterBar from '../shared/FilterBar';
import StatTile from '../shared/StatTile';
import DataTable from '../shared/DataTable';
import Pagination, { normalizePagination } from '../shared/Pagination';
import DetailDrawer from '../shared/DetailDrawer';
import { buildParams } from '../shared/apiParams';

const API_URL = config.apiUrl;
const EMPTY = { blocks: [], gramPanchayats: [], villages: [], enumerators: [], startDate: '', endDate: '' };

const formatDate = (date) => {
  if (!date) return 'N/A';
  const d = new Date(date);
  if (isNaN(d.getTime())) return 'Invalid Date';
  const p = (n) => n.toString().padStart(2, '0');
  return `${p(d.getMonth() + 1)}/${p(d.getDate())}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};
const calcDuration = (start, end) => {
  if (!start || !end) return 'N/A';
  const ms = new Date(end) - new Date(start);
  if (isNaN(ms)) return 'Invalid';
  const min = Math.abs(ms / 60000);
  const h = Math.floor(min / 60);
  const m = Math.floor(min % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const TABS = [
  { key: 'enumerator', label: 'By Enumerator', col: 'Enumerator ID' },
  { key: 'block', label: 'By Block', col: 'Block Name' },
  { key: 'gp', label: 'By Gram Panchayat', col: 'Gram Panchayat' },
  { key: 'village', label: 'By Village', col: 'Village Name' },
];

const METRIC_LABEL = {
  anganwadi: 'Anganwadi Registered',
  supplementary: 'Supplementary Marked Not Applicable',
  thr: 'THR Marked Not Applicable',
};

const pctCell = (b, highlight) => (
  <>
    {b?.count || 0}
    <span style={{ fontSize: '0.72rem', color: highlight ? 'var(--danger)' : 'var(--muted)', fontWeight: highlight ? 700 : 400, marginLeft: 4 }}>
      ({b?.pct || 0}%)
    </span>
  </>
);

export default function Anganwadi({ rule }) {
  const [activeTab, setActiveTab] = useState('enumerator');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState(normalizePagination({}));
  const [applied, setApplied] = useState(EMPTY);
  const [drawer, setDrawer] = useState({ open: false, row: null, metric: null, records: [], loading: false });
  const [recordPopup, setRecordPopup] = useState({ open: false, record: null });

  const tab = TABS.find((t) => t.key === activeTab);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams(applied, { extra: { page, limit: pageSize } });
      if (activeTab !== 'enumerator') params.append('forceGroupBy', activeTab);
      const res = await axios.get(`${API_URL}/anganwadi/analytics?${params}`);
      if (res.data?.success) {
        setRows(res.data.data || []);
        setSummary(res.data.summary || null);
        setPagination(normalizePagination(res.data.pagination || {}));
      }
    } catch (err) {
      console.error('Error fetching anganwadi data:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, applied, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalForms = summary?.totalForms ?? rows.reduce((s, r) => s + (Number(r.totalForms) || 0), 0);

  const tiles = (
    <>
      <StatTile label="Total Forms" value={totalForms} icon={GroupsIcon} accent="var(--primary)" />
      <StatTile
        label="Anganwadi Registered"
        value={`${summary?.anganwadiRegistered?.pct ?? 0}%`}
        icon={ChildCareIcon}
        accent="var(--info)"
        sub={`${summary?.anganwadiRegistered?.count ?? 0} of ${totalForms} forms`}
      />
      <StatTile
        label="Supplementary Marked N/A"
        value={`${summary?.supplementaryNA?.pct ?? 0}%`}
        icon={ChildCareIcon}
        accent={(summary?.supplementaryNA?.pct ?? 0) >= 25 ? 'var(--danger)' : 'var(--warning)'}
        sub="of Anganwadi-registered forms"
      />
      <StatTile
        label="THR Marked N/A"
        value={`${summary?.thrNA?.pct ?? 0}%`}
        icon={ChildCareIcon}
        accent={(summary?.thrNA?.pct ?? 0) >= 25 ? 'var(--danger)' : 'var(--warning)'}
        sub="of Anganwadi-registered forms"
      />
    </>
  );

  const openDrilldown = async (row, metric) => {
    setDrawer({ open: true, row, metric, records: [], loading: true });
    try {
      const params = buildParams(applied, {
        extra: { groupBy: activeTab, groupValue: row.name, metric, limit: 100 },
      });
      const res = await axios.get(`${API_URL}/anganwadi/details?${params}`);
      const records = res.data?.data || [];
      setDrawer({ open: true, row, metric, records, loading: false });
    } catch (err) {
      console.error('Error fetching anganwadi detail records:', err);
      setDrawer({ open: true, row, metric, records: [], loading: false });
    }
  };

  const metricCell = (row, dataKey, metricKey, highlight) => (
    <span className="clickable-name" onClick={(e) => { e.stopPropagation(); openDrilldown(row, metricKey); }}>
      {pctCell(row[dataKey], highlight)}
    </span>
  );

  const columns = [
    { key: 'first', label: tab.col, render: (r) => <span className="cell-strong">{r.name || 'N/A'}</span> },
    { key: 'totalForms', label: 'Total Forms', align: 'center', render: (r) => <span className="cell-strong">{r.totalForms || 0}</span> },
    { key: 'anganwadiRegistered', label: 'Anganwadi Registered (% of Total)', align: 'center', render: (r) => metricCell(r, 'anganwadiRegistered', 'anganwadi', false) },
    { key: 'supplementaryNA', label: 'Supplementary Marked N/A (% of Registered)', align: 'center', render: (r) => metricCell(r, 'supplementaryNA', 'supplementary', true) },
    { key: 'thrNA', label: 'THR Marked N/A (% of Registered)', align: 'center', render: (r) => metricCell(r, 'thrNA', 'thr', true) },
  ];

  const metricValue = (r) => {
    if (drawer.metric === 'anganwadi') return r.anganwadiRegistration;
    if (drawer.metric === 'supplementary') return r.supplementaryNutritionReceived;
    if (drawer.metric === 'thr') return r.takeHomeRationReceived;
    return '—';
  };

  const drawerRecordColumns = [
    { key: 'srNo', label: 'Sr No', align: 'center', render: (_r, i) => i + 1 },
    { key: 'household_id', label: 'Household ID', render: (r) => r.household_id || '—' },
    { key: 'respondent_name', label: 'Respondent', render: (r) => r.respondent_name || '—' },
    { key: 'value', label: 'Value', render: (r) => metricValue(r) || '—' },
    { key: 'block', label: 'Block', render: (r) => r.block || '—' },
    { key: 'village_name', label: 'Village', render: (r) => r.village_name || '—' },
    { key: 'start', label: 'Start Time', render: (r) => formatDate(r.start) },
    { key: 'end', label: 'End Time', render: (r) => formatDate(r.end) },
    { key: 'duration', label: 'Duration', align: 'center', render: (r) => calcDuration(r.start, r.end) },
  ];

  const filterBar = (
    <FilterBar
      onApply={(v) => { setApplied(v); setPage(1); }}
      onClear={() => { setApplied(EMPTY); setPage(1); }}
      loading={loading}
    />
  );

  return (
    <RuleScreen
      title={rule?.title || 'Anganwadi Registered but Nutrition N/A'}
      subtitle="Among Anganwadi-registered households, how many have Supplementary Nutrition or Take-Home Ration marked 'Not applicable' — inconsistent with being registered."
      icon={ChildCareIcon}
      backTo={rule?.category ? `/category/${rule.category}` : undefined}
      backLabel="Back"
      tiles={tiles}
      filterBar={
        <>
          <div className="tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                className={`tab-btn${activeTab === t.key ? ' active' : ''}`}
                onClick={() => { if (t.key !== activeTab) { setActiveTab(t.key); setPage(1); } }}
              >
                {t.label}
              </button>
            ))}
          </div>
          {filterBar}
        </>
      }
    >
      <div className="card">
        <h3 className="card-title">Anganwadi vs Nutrition Benefits by {tab.label.replace('By ', '')}</h3>
        <Pagination
          pagination={pagination}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          unitLabel="groups"
          shown={rows.length}
        />
        <DataTable
          columns={columns}
          rows={rows}
          loading={loading}
          emptyMessage='No data found. Adjust filters and click "Apply Filters".'
          getRowKey={(r, i) => `${r.name}-${i}`}
        />
      </div>

      <DetailDrawer
        open={drawer.open}
        onClose={() => setDrawer((d) => ({ ...d, open: false }))}
        title={drawer.row ? `${tab.col}: ${drawer.row.name} — ${METRIC_LABEL[drawer.metric] || ''}` : ''}
        sections={
          drawer.row
            ? [
                {
                  title: 'Breakdown',
                  items: [
                    { label: 'Total Forms', value: drawer.row.totalForms || 0 },
                    { label: 'Anganwadi Registered', value: `${drawer.row.anganwadiRegistered?.count || 0} (${drawer.row.anganwadiRegistered?.pct || 0}%)` },
                    { label: 'Supplementary Marked N/A', value: `${drawer.row.supplementaryNA?.count || 0} (${drawer.row.supplementaryNA?.pct || 0}%)` },
                    { label: 'THR Marked N/A', value: `${drawer.row.thrNA?.count || 0} (${drawer.row.thrNA?.pct || 0}%)` },
                  ],
                },
              ]
            : []
        }
      >
        <div className="detail-section">
          <h4>Households</h4>
          <DataTable
            columns={drawerRecordColumns}
            rows={drawer.records}
            loading={drawer.loading}
            emptyMessage="No households found."
            onRowClick={(r) => setRecordPopup({ open: true, record: r })}
            getRowKey={(r, i) => r.uuid || i}
          />
        </div>
      </DetailDrawer>

      <DetailDrawer
        open={recordPopup.open}
        onClose={() => setRecordPopup((p) => ({ ...p, open: false }))}
        title={recordPopup.record ? `Household: ${recordPopup.record.household_id}` : ''}
        sections={
          recordPopup.record
            ? (() => {
                const r = recordPopup.record;
                const isRegistered = r.anganwadiRegistration === 'Yes';
                const supplementaryNA = isRegistered && r.supplementaryNutritionReceived === 'Not applicable';
                const thrNA = isRegistered && r.takeHomeRationReceived === 'Not applicable';
                const statusBadge = (flagged, yesLabel, noLabel) => (
                  <span className={`badge ${flagged ? 'badge-danger' : 'badge-success'}`}>
                    {flagged ? yesLabel : noLabel}
                  </span>
                );
                return [
                  {
                    title: 'Status',
                    items: [
                      { label: 'Anganwadi Registered (% of Total)', value: statusBadge(isRegistered, 'Registered', 'Not Registered') },
                      { label: 'Supplementary Marked N/A (% of Registered)', value: isRegistered ? statusBadge(supplementaryNA, 'Marked N/A', 'OK') : <span className="badge badge-neutral">N/A (not registered)</span> },
                      { label: 'THR Marked N/A (% of Registered)', value: isRegistered ? statusBadge(thrNA, 'Marked N/A', 'OK') : <span className="badge badge-neutral">N/A (not registered)</span> },
                    ],
                  },
                  {
                    title: 'Household',
                    items: [
                      { label: 'Household ID', value: r.household_id },
                      { label: 'Respondent', value: r.respondent_name },
                      { label: 'Enumerator', value: r.enumerator_id },
                      { label: 'Block', value: r.block },
                      { label: 'Gram Panchayat', value: r.gram_panchayat },
                      { label: 'Village', value: r.village_name },
                    ],
                  },
                  {
                    title: 'Anganwadi & Nutrition (raw values)',
                    items: [
                      { label: 'Anganwadi Registered', value: r.anganwadiRegistration },
                      { label: 'Supplementary Nutrition', value: r.supplementaryNutritionReceived },
                      { label: 'Take-Home Ration', value: r.takeHomeRationReceived },
                    ],
                  },
                  {
                    title: 'Survey Timing',
                    items: [
                      { label: 'Start Time', value: formatDate(r.start) },
                      { label: 'End Time', value: formatDate(r.end) },
                      { label: 'Duration', value: calcDuration(r.start, r.end) },
                    ],
                  },
                ];
              })()
            : []
        }
      />
    </RuleScreen>
  );
}
